/**
 * Die Vorschlagskarte im Dokumentblatt (`document-suggestion`, Spec 24c,
 * Oberfläche A) — ganz oben im Blatt „Dokument ändern", über
 * `document-preview`. Nur, wenn ein Vorschlag steht.
 *
 * Bauform von `ArchivedBanner` (`border-border bg-bg rounded-card px-4 py-3`,
 * `role="status"`): ein Hinweis, kein Formularfeld. **Kein** Akzentrand,
 * keine Fläche in Akzentfarbe — den Akzent trägt allein der Knopf
 * „Übernehmen" (Entwurf `design-v01.html`, Entscheidung D1: Akzentknopf,
 * `document-save` bleibt die primäre Aktion des Blatts).
 *
 * Rein präsentational: `DocumentSheet` besitzt die Mutationen
 * (`useAcceptSuggestion`/`useDismissSuggestion`) und die Fehleranzeige
 * (`document-error`) — dieselbe Aufteilung wie `handleArchive`/
 * `handlePreview` dort (Container/Presentational-Split).
 */

import type { DocumentSuggestion } from '@/features/documents/api'

interface SuggestionCardProps {
  readonly suggestion: DocumentSuggestion
  readonly onAccept: () => void
  readonly onDismiss: () => void
  readonly isPending: boolean
}

export function SuggestionCard({ suggestion, onAccept, onDismiss, isPending }: SuggestionCardProps) {
  return (
    <div
      data-testid="document-suggestion"
      role="status"
      aria-busy={isPending}
      className="border-border bg-bg rounded-card flex flex-col gap-3 border px-4 py-3 md:flex-row md:items-center md:justify-between"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-label text-muted flex items-center gap-2 uppercase">
          <span>Vorschlag</span>
          <span>{suggestion.kind.label}</span>
        </span>
        <span className="text-body text-text break-words font-(--font-weight-section)">{suggestion.summary_display}</span>
        <span className="text-label text-muted">{suggestion.reason_display}</span>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:shrink-0">
        <button
          type="button"
          data-testid="document-suggestion-accept"
          disabled={isPending}
          aria-busy={isPending}
          onClick={onAccept}
          className="bg-accent rounded-card text-body min-h-(--tap-min) px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
        >
          Übernehmen
        </button>
        <button
          type="button"
          data-testid="document-suggestion-dismiss"
          disabled={isPending}
          aria-busy={isPending}
          onClick={onDismiss}
          className="border-border text-text rounded-card text-body min-h-(--tap-min) border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
        >
          Verwerfen
        </button>
      </div>
    </div>
  )
}
